import * as React from 'react';
import { Box, Text } from '@radix-ui/themes';

import TransactionStatusBadge from '../../components/TransactionStatusBadge';
import HashShortener from '../../components/HashShortener';
import { AddressDisplay } from '../../components/AddressDisplay';
import ViewButton from '../../components/ViewButton';
import { GraphQLTable, Column } from '../../components/GraphQLTable';
import {
  GetTransactionsDocument,
  GetTransactionsQuery,
  GetTransactionsQueryVariables,
} from '../../graphql/queries/GetTransactions.generated';
import { FilterOperation, TransactionStatus } from '../../types';

const TransactionList: React.FC = () => {
  const columns: Column<
    GetTransactionsQuery['transactions']['edges'][0]['node']
  >[] = [
    {
      header: 'Hash',
      accessor: 'hash',
      render: ({ value }) => (
        <HashShortener hash={value} link={`/transactions/${value}`} />
      ),
      filterable: true,
      filterField: 'hash',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search hash...',
      },
    },
    {
      header: 'From',
      accessor: 'from',
      render: ({ value }) => <AddressDisplay address={value} />,
      filterable: true,
      filterField: 'from',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search address...',
      },
    },
    {
      header: 'To',
      accessor: 'to',
      render: ({ value }) =>
        value ? <AddressDisplay address={value} /> : 'Contract Creation',
      filterable: true,
      filterField: 'to',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search address...',
      },
    },
    {
      header: 'Value (ETH)',
      accessor: 'valueEth',
      render: ({ value }) => {
        const numValue = parseFloat(value);
        const formatted = numValue.toLocaleString('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        });
        return (
          <Text
            size="2"
            style={{
              fontFamily: 'var(--code-font-family)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatted}
          </Text>
        );
      },
      filterable: true,
      filterField: 'valueEth',
      filterConfig: {
        type: 'number',
        operation: FilterOperation.Gte,
        placeholder: 'Min value...',
      },
    },
    {
      header: 'Value (USD)',
      accessor: 'valueUsd' as any, // Cast as any since valueUsd may not be in types yet
      render: ({ row }) => {
        // Access valueUsd safely in case it's not in the generated types yet
        const valueUsd = (row as any).valueUsd;
        if (valueUsd === null || valueUsd === undefined) return <Text size="2">—</Text>;
        const numValue = typeof valueUsd === 'string' ? parseFloat(valueUsd) : valueUsd;
        if (isNaN(numValue)) return <Text size="2">—</Text>;

        const formatted = numValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return (
          <Text
            size="2"
            style={{
              fontFamily: 'var(--code-font-family)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            ${formatted}
          </Text>
        );
      },
      filterable: true,
      filterField: 'valueUsd',
      filterConfig: {
        type: 'number',
        operation: FilterOperation.Gte,
        placeholder: 'Min value...',
      },
    },
    {
      header: 'Gas',
      accessor: 'gas',
      render: ({ value }) => {
        return <span>{value.toLocaleString()}</span>;
      },
      filterable: true,
      filterField: 'gas',
      filterConfig: {
        type: 'number',
        operation: FilterOperation.Gte,
        placeholder: 'Min gas...',
      },
    },
    {
      header: 'Status',
      accessor: 'status',
      render: ({ value }) => <TransactionStatusBadge status={value} />,
      filterable: true,
      filterField: 'status',
      filterConfig: {
        type: 'select',
        operation: FilterOperation.Eq,
        placeholder: 'Filter by status...',
        options: [
          { label: 'Confirmed', value: TransactionStatus.Confirmed },
          { label: 'Pending', value: TransactionStatus.Pending },
          { label: 'Failed', value: TransactionStatus.Failed },
        ],
      },
    },
    {
      header: null,
      render: ({ row }) => (
        <ViewButton to={`/transactions/${row.hash}`} text="View Transaction" />
      ),
    },
  ];

  return (
    <Box>
      <GraphQLTable<
        GetTransactionsQuery,
        GetTransactionsQueryVariables,
        'transactions',
        GetTransactionsQuery['transactions']['edges'][0]['node']
      >
        query={GetTransactionsDocument}
        columns={columns}
        connectionPath="transactions"
        loadingMessage="Fetching latest transactions..."
        errorMessage="Failed to fetch transactions."
        noDataMessage="No transactions available."
      />
    </Box>
  );
};

export default TransactionList;
